// src/hooks/useContacts.ts
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAtom } from 'jotai';
import type { Contact, ContactFilters } from '@/types/contact';
import { useToast } from './use-toast';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';
import { contactsListAtom } from '@/lib/atoms';
import { toRpcFilters } from '@/lib/filters/rpc';

function adjustToLocalTimezone(dateStr: string | null): string | null {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return date.toISOString();
}

const BATCH_SIZE = 200;

/**
 * Contacts hook with infinite/progressive loading.
 *
 * Uses Jotai atoms for contacts/totalCount/currentPage so data persists
 * across component unmount/remount (e.g. navigating to contact detail and back).
 *
 * Manages pagination internally — the consumer does not pass pageSize/pageNumber.
 * First render fetches page 1 (200 contacts). Call `loadMore()` to append the next page.
 * Changing filters/search/sort resets to page 1 automatically.
 */
export function useContacts(filters: ContactFilters = {}) {
  const { currentWorkspace } = useWorkspaces();
  const { toast } = useToast();

  const [listState, setListState] = useAtom(contactsListAtom);

  const [loading, setLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Track the last fetch to prevent race conditions
  const fetchIdRef = useRef(0);

  // Stable key for detecting filter/sort/search changes (EXCLUDE pageSize/pageNumber)
  const filtersKey = JSON.stringify({
    search: filters.search || '',
    group_type: filters.group_type || 'AND',
    groups: filters.groups || [],
    sort: filters.sort || null,
    contactIds: filters.contactIds || null,
    workspaceId: currentWorkspace?.id || '',
  });

  // Check if we have cached data for current filters
  const hasCachedData = listState.filtersKey === filtersKey && listState.contacts.length > 0;

  const fetchPage = useCallback(async (page: number, isAppend: boolean) => {
    if (!currentWorkspace?.id) {
      setListState({ contacts: [], totalCount: 0, currentPage: 1, filtersKey });
      setLoading(false);
      return;
    }

    const fetchId = ++fetchIdRef.current;

    if (isAppend) {
      setIsLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      // When filtering by contactIds, fetch a larger page to ensure we capture all group members
      const hasContactIds = filters.contactIds && filters.contactIds.length > 0;
      const effectivePageSize = hasContactIds
        ? Math.max(filters.contactIds!.length, 500)
        : BATCH_SIZE;
      const effectivePageNumber = hasContactIds ? 1 : page;

      // Normalize client-shape filters to RPC wire shape at the boundary.
      // Renames valueEnd → end_value, matchYear → match_year, group.type →
      // group_type, drops SQL-inert client-only fields. The only bridge between
      // the saved-view shape and what `filter_contacts` reads. See rpc.ts.
      const rpcFilters = toRpcFilters(filters);

      const result = await supabase.rpc('filter_contacts', {
        p_workspace_id: currentWorkspace.id,
        filters: rpcFilters,
        page_size: effectivePageSize,
        page_number: effectivePageNumber,
        count_only: false,
        sort_config: filters.sort && filters.sort.length > 0 ? filters.sort : null
      });

      // Check if this is still the latest fetch
      if (fetchId !== fetchIdRef.current) return;

      if (result.error) throw result.error;

      const data = result.data;
      if (!data) {
        // RPC returned no data but no error either. Silent-failure surface —
        // emit telemetry so we can see this in GlitchTip even though the user
        // sees an empty list (which might be legitimate).
        captureError(new Error('filter_contacts RPC returned null data'), {
          feature: 'contacts-list-fetch-null-data',
          filterGroupsCount: filters.groups?.length ?? 0,
          search: filters.search ? 'set' : 'empty',
        });
        if (!isAppend) {
          setListState({ contacts: [], totalCount: 0, currentPage: page, filtersKey });
        }
        setLoading(false);
        setIsLoadingMore(false);
        return;
      }

      // Extract total_count from the first result
      const count = data.length > 0 ? data[0].total_count : 0;

      const cleanedContacts: Contact[] = data.map((contact: any) => {
        const { total_count, ...rest } = contact;
        return {
          ...rest,
          created_at: adjustToLocalTimezone(rest.created_at),
          last_contacted: adjustToLocalTimezone(rest.last_contacted),
          last_contact_type: rest.last_contact_type,
          skip_until: rest.skip_until,
          next_contact: rest.next_contact,
        };
      });

      // Apply contactIds filter (post-filter for group membership)
      let finalContacts = cleanedContacts;
      let finalCount = count;
      if (filters.contactIds && filters.contactIds.length > 0) {
        const idSet = new Set(filters.contactIds);
        finalContacts = cleanedContacts.filter((c: Contact) => idSet.has(c.id));
        finalCount = finalContacts.length;
      }

      if (isAppend) {
        // Append to existing, deduplicate by id
        setListState(prev => {
          const existingIds = new Set(prev.contacts.map(c => c.id));
          const newUniqueContacts = finalContacts.filter(c => !existingIds.has(c.id));
          return {
            ...prev,
            contacts: [...prev.contacts, ...newUniqueContacts],
            currentPage: page,
            totalCount: finalCount > 0 ? finalCount : prev.totalCount,
          };
        });
      } else {
        setListState({
          contacts: finalContacts,
          totalCount: finalCount,
          currentPage: page,
          filtersKey,
        });
      }

    } catch (err) {
      // Check if this is still the latest fetch
      if (fetchId !== fetchIdRef.current) return;

      setError(err instanceof Error ? err : new Error('Failed to fetch contacts'));
      captureError(err as Error, { feature: 'contacts-list-fetch' });

      toast({
        title: "Error",
        description: "Failed to load contacts. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace?.id, filtersKey, toast, setListState]);

  // Initial fetch — skip if we have cached data for the same filters
  // Use filtersKey as dependency (stable string) instead of fetchPage (unstable function ref)
  // This prevents re-fetch loops when filters.sort/groups create new object references
  useEffect(() => {
    if (hasCachedData) {
      console.log('[useContacts] Using cached data — skipping fetch');
      setLoading(false);
      return;
    }
    console.log('[useContacts] No cache match — fetching page 1');
    fetchPage(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  const loadMore = useCallback(() => {
    if (isLoadingMore || loading) return;
    const nextPage = listState.currentPage + 1;
    fetchPage(nextPage, true);
  }, [listState.currentPage, fetchPage, isLoadingMore, loading]);

  const hasMore = listState.contacts.length < listState.totalCount;

  const invalidateCache = useCallback(() => {
    setListState({ contacts: [], totalCount: 0, currentPage: 1, filtersKey: '' });
    fetchPage(1, false);
  }, [fetchPage, setListState]);

  return {
    contacts: listState.contacts,
    loading: !hasCachedData && loading,
    isLoadingMore,
    totalCount: listState.totalCount,
    error,
    hasMore,
    loadMore,
    invalidateCache,
    refetch: invalidateCache,
  };
}
